import { useScrollContext } from "@navigations/utils/ScrollContext";
import useHideNavsOnScroll from "@navigations/utils/useHideNavsOnScroll";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from "react-native";
import { useTheme } from "styled-components/native";
import { isStickySummaryVisible } from "../utils/stickySummaryVisibility";

interface UsePdpScrollStateProps {
  stickyContainerHeight: number;
}

type StickyFrame = {
  y: number;
  height: number;
  stackedAboveScroll: boolean;
};

export const usePdpScrollState = ({
  stickyContainerHeight,
}: UsePdpScrollStateProps) => {
  const theme = useTheme();
  const isFocused = useIsFocused();
  const [_, handleReset] = useScrollContext();
  const { jsScrollHandler, resetScrollState } = useHideNavsOnScroll();
  const [showProductHeaderStickySummary, setShowProductHeaderStickySummary] =
    useState(false);

  const scrollViewRef = useRef<any>(null);
  const headerProductView = useRef<any>(null);
  const headerStickySummaryViewRef = useRef<any>(null);
  const headerOffsetRef = useRef<number | null>(null);
  const featureButtonsOffsetRef = useRef<number | null>(null);
  const stickyFrameRef = useRef<StickyFrame | null>(null);
  const scrollYRef = useRef(0);

  const maxContentLength = 1432;

  const syncStickySummary = useCallback(
    (scrollY: number) => {
      scrollYRef.current = scrollY;
      const productY =
        featureButtonsOffsetRef.current ?? headerOffsetRef.current;
      const stickyFrame = stickyFrameRef.current;
      if (productY == null || stickyFrame == null) {
        return;
      }

      const show = isStickySummaryVisible(
        productY,
        scrollY,
        stickyFrame.y,
        stickyContainerHeight,
        {
          stackedAboveScroll: stickyFrame.stackedAboveScroll,
          stickyHeight: stickyFrame.height,
          fudge: 20,
          hideUntilScrolled: true,
        }
      );
      setShowProductHeaderStickySummary((current) =>
        current === show ? current : show
      );
    },
    [stickyContainerHeight]
  );

  const scrollToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo?.({
      x: 0,
      y: 0,
      animated: false,
    });

    scrollYRef.current = 0;
    setShowProductHeaderStickySummary(false);
  }, []);

  const onHeaderProductOffset = useCallback(
    (y: number | null) => {
      headerOffsetRef.current = y;
      syncStickySummary(scrollYRef.current);
    },
    [syncStickySummary]
  );

  const onFeatureButtonsOffset = useCallback(
    (y: number | null) => {
      featureButtonsOffsetRef.current = y;
      syncStickySummary(scrollYRef.current);
    },
    [syncStickySummary]
  );

  const onHeaderStickyLayout = useCallback(
    (event: LayoutChangeEvent, stackedAboveScroll = false) => {
      stickyFrameRef.current = {
        y: event.nativeEvent.layout.y,
        height: event.nativeEvent.layout.height,
        stackedAboveScroll,
      };
      syncStickySummary(scrollYRef.current);
    },
    [syncStickySummary]
  );

  const onScrollHandler = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (theme.isLargeScreen) jsScrollHandler(event);
      syncStickySummary(event.nativeEvent.contentOffset.y);
    },
    [theme.isLargeScreen, jsScrollHandler, syncStickySummary]
  );

  const onScroll = isFocused ? onScrollHandler : undefined;

  useFocusEffect(
    useCallback(() => {
      const isWeb = Platform.OS === "web";
      const isMobileWeb = isWeb && theme.isMobile;
      if (!isWeb || isMobileWeb) {
        handleReset();
        resetScrollState();
      }
    }, [theme.isMobile])
  );

  useEffect(() => {
    if (isFocused && theme.isMobile) {
      scrollToTop();
    }
  }, [isFocused, theme.isMobile, scrollToTop]);

  return {
    // state
    showProductHeaderStickySummary,
    setShowProductHeaderStickySummary,

    // refs
    scrollViewRef,
    headerProductView,
    headerStickySummaryViewRef,

    // constants
    maxContentLength,

    // handlers
    onScroll,
    scrollToTop,
    onHeaderProductOffset,
    onFeatureButtonsOffset,
    onHeaderStickyLayout,
  };
};
